import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
  InputAdornment,
} from "@mui/material";
import { AccountCircle, Lock } from "@mui/icons-material";
import API_BASE_URL from "../../../config/api";

const ForgotPassword = () => {
  const [employeeId, setEmployeeId] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getCurrentYear = () => new Date().getFullYear();

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/admin/reset_password.php`,
        { employeeId, newPassword }
      );

      if (response.data.success) {
        setMessage("Password reset successfully! Redirecting to login...");
        setTimeout(() => navigate("/admin-login"), 2000);
      } else {
        setError(response.data.message || "Failed to reset password.");
      }
    } catch (error) {
      setError("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        position: "relative",
        backgroundImage: `url(${API_BASE_URL}/uploads/headerImg/bodyfriend_outlet.jpg)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(10, 10, 10, 0.50)",
          zIndex: 1,
        }}
      ></Box>

      <Card
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 3,
          boxShadow: 3,
          borderRadius: 3,
          position: "relative",
          zIndex: 2,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          margin: 2,
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
          >
            Reset Password
          </Typography>

          {message && (
            <Typography
              variant="body2"
              color="success.main"
              textAlign="center"
              mb={2}
            >
              {message}
            </Typography>
          )}
          {error && (
            <Typography variant="body2" color="error" textAlign="center" mb={2}>
              {error}
            </Typography>
          )}

          <Box
            component="form"
            onSubmit={handleResetRequest}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Employee ID"
              variant="outlined"
              fullWidth
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="New Password"
              type="password"
              variant="outlined"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Confirm Password"
              type="password"
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2, py: 1.5 }}
              disabled={loading}
            >
              {loading ? "Processing..." : "Reset Password"}
            </Button>
          </Box>

          <Typography variant="body2" textAlign="center" mt={3}>
            <Button onClick={() => navigate("/admin-login")} size="small">
              Back to Login
            </Button>
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            mt={3}
          >
            © {getCurrentYear()} Bodyfriend Admin Dashboard
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPassword;
